import moment from 'moment-timezone'
import React, { useReducer, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  makeStyles,
  Paper,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
} from '@material-ui/core'
import { ExpandMore as ExpandMoreIcon, Add as AddIcon } from '@material-ui/icons'

import { Checkbox } from 'civic-champs-shared/core/new-checkbox'
import { OutlinedButton, ContainedButton } from 'civic-champs-shared'
import { AdvancedAutocomplete } from 'civic-champs-shared'
import { useFeatureEnabled } from 'civic-champs-shared/feature/hooks'

import PageHeader from 'App/components/PageHeader'
import useGetEventQueueStatus, {
  GetEventQueueStatusFlags as Flags,
  useSubmitLoadSimulator,
} from './useGetEventQueueStatus'
import { useAutorefresh } from './useAutorefresh'
import { LoadSimulatorModal } from './LoadSimulatorModal'
import { TaskTable } from './TaskTable'
import { groupWorkersByInstance, resolveWorkerLabel } from './workerGrouping.util'
import { debounce, get } from 'lodash'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  topSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBotton: '20px',
    alignItems: 'center',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '15px',
  },
  statusColumns: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: '25px',
  },
  controlsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  dataColumn: {
    width: '332px',
    display: 'flex',
    padding: '0px 2px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '20px',
  },
  dataItem: {
    display: 'flex',
    fontSize: '14px',
    fontFamily: 'Nunito',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    '& > div': {
      flexShrink: 0,
    },
    '& > div:nth-child(1)': {
      fontWeight: 700,
      width: '132px',
    },
    '& > div:nth-child(2)': {
      width: '200px',
    },
  },
  flags: {
    display: 'flex',
    gap: '45px',
    marginBottom: '25px',
  },
  flagCheckbox: {
    textTransform: 'capitalize',
  },
  statusFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'bottom',
  },
  autoRefreshSelect: {
    width: '200px',
    height: 'auto',
  },
  refreshBtn: {
    height: 'fit-content',
    alignSelf: 'center',
  },
  addButton: {
    marginBottom: '10px',
    width: 'fit-content',
    alignSelf: 'flex-end',
  },
  expansionPanel: {
    marginBottom: '10px',
  },
  expansionSummary: {
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
  },
  taskCount: {
    marginLeft: '8px',
    color: '#666',
    fontWeight: 400,
  },
  workersHint: {
    margin: '0 0 12px 0',
    color: '#555',
    fontSize: '14px',
  },
  workersTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    '& th, & td': {
      textAlign: 'left',
      padding: '8px 12px',
      borderBottom: '1px solid #e0e0e0',
    },
    '& th': {
      fontWeight: 700,
    },
  },
  instanceHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 700,
    color: '#333',
  },
})

interface State extends Flags {
  autorefreshRate: number
  lastUpdated?: Date
}

const reducer = (state: State, action: Partial<State>) => ({ ...state, ...action })
const initialState = { queued: false, running: false, completed: false, autorefreshRate: -1 }

const EventQueueMonitoringComponent = () => {
  const isLoadSimulatorEnabled = useFeatureEnabled('EventHandlerLoadSimulator')

  const [{ lastUpdated, autorefreshRate, ...flags }, dispatch] = useReducer(reducer, initialState)
  const [modalOpen, setModalOpen] = useState(false)

  const [getQueueStatus, { loading, result: queueStatus }] = useGetEventQueueStatus()
  const [submitLoadSimulator, { loading: submitting }] = useSubmitLoadSimulator()

  const flagsRef = useRef(flags)
  flagsRef.current = flags

  const refresh = useCallback(async () => {
    await getQueueStatus(flagsRef.current)
    dispatch({ lastUpdated: new Date() })
  }, [getQueueStatus, dispatch])

  const handleSubmitLoadSimulator = async (payload: any) => {
    await submitLoadSimulator(payload)
    await refresh()
  }

  useAutorefresh(refresh, autorefreshRate * 1000)
  const debouncedRefresh = useMemo(() => debounce(refresh, 1000, { trailing: true }), [refresh])
  useEffect(() => {
    debouncedRefresh()
  }, [flags.queued, flags.running, flags.completed, debouncedRefresh])

  const classes = useStyles()
  const showWorkerId = (queueStatus?.workerCount || 0) > 1
  const workers = queueStatus?.workers || []
  const instanceGroups = groupWorkersByInstance(workers)
  const groupedByInstance = instanceGroups.length > 1
  const formatOldestTask = (value?: string | Date | null) => {
    if (!value) return 'N/A'
    return moment(value).format('M/D/YY h:mm:ss a')
  }

  return (
    <div className={classes.container}>
      <div className={classes.topSection}>
        {isLoadSimulatorEnabled ? (
          <span>Disabled 'EventHandlerLoadSimulator' to cancel active test loads.</span>
        ) : (
          <span>Enabled 'EventHandlerLoadSimulator' to run queue tests.</span>
        )}
        <ContainedButton
          className={classes.addButton}
          onClick={() => setModalOpen(true)}
          startIcon={<AddIcon />}
          disabled={!isLoadSimulatorEnabled || loading || submitting}
        >
          Create Test Load
        </ContainedButton>
      </div>

      <Paper className={classes.statusContainer}>
        {showWorkerId && (
          <p className={classes.workersHint}>
            Aggregated across {queueStatus?.workerCount} workers
            {groupedByInstance ? ` across ${instanceGroups.length} instances` : ''}
            {queueStatus?.aggregated ? '' : ' (single worker reporting)'}
          </p>
        )}
        <div className={classes.statusColumns}>
          <div className={classes.dataColumn}>
            <div className={classes.dataItem}>
              <div>Max Concurrency:</div>
              <div>{get(queueStatus, 'maxConcurrency', '?')}</div>
            </div>
            <div className={classes.dataItem}>
              <div>Priority Reserve:</div>
              <div>{queueStatus?.highPriorityReserve || '?'}</div>
            </div>
            <div className={classes.dataItem}>
              <div>Running Count:</div>
              <div>{get(queueStatus, 'runningCount', '?')}</div>
            </div>
            <div className={classes.dataItem}>
              <div>Queue Length:</div>
              <div>{get(queueStatus, 'queueLength', '?')}</div>
            </div>
            <div className={classes.dataItem}>
              <div>Oldest Task:</div>
              <div>{formatOldestTask(queueStatus?.oldestTask)}</div>
            </div>
          </div>
          <div className={classes.controlsColumn}>
            <div className={classes.flags}>
              {(['queued', 'running', 'completed'] as Array<keyof Flags>).map(key => (
                <Checkbox
                  key={key}
                  value={flags[key]}
                  onClick={checked => dispatch({ [key]: checked })}
                  label={`${key} tasks`}
                  className={classes.flagCheckbox}
                />
              ))}
            </div>
            <AdvancedAutocomplete
              className={classes.autoRefreshSelect}
              label="Auto-refresh Rate"
              options={[-1, 10, 30, 60, 300]}
              value={autorefreshRate}
              getOptionInfo={(option: number) => ({
                left: option <= 0 ? 'Never' : `${option}s`,
                id: option,
              })}
              onChange={(value: number) => dispatch({ autorefreshRate: value })}
              disableOptionsAutoFiltering
              disableInputFilter
              clearable={false}
              disabled={loading}
            />
          </div>
        </div>
        <div className={classes.statusFooter}>
          <p>{lastUpdated ? 'Last updated ' + moment(lastUpdated).format('M/D/YYYY h:mm:ss a z') : ''}</p>
          <OutlinedButton className={classes.refreshBtn} disabled={loading} onClick={refresh}>
            Refresh
          </OutlinedButton>
        </div>
      </Paper>

      {showWorkerId && workers.length > 0 && (
        <Paper className={classes.statusContainer}>
          <Typography variant="h6" style={{ marginBottom: 12 }}>
            Per worker
          </Typography>
          <table className={classes.workersTable}>
            <thead>
              <tr>
                <th>{groupedByInstance ? 'pm2 worker' : 'Worker'}</th>
                <th>Running</th>
                <th>Queued</th>
                <th>Oldest task</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {instanceGroups.map(group => (
                <React.Fragment key={group.instanceId}>
                  {groupedByInstance && (
                    <tr className={classes.instanceHeader}>
                      <td colSpan={5}>
                        Instance {group.instanceId} ({group.workers.length}{' '}
                        {group.workers.length === 1 ? 'worker' : 'workers'})
                      </td>
                    </tr>
                  )}
                  {group.workers.map(worker => (
                    <tr key={worker.workerId}>
                      <td>{resolveWorkerLabel(worker.workerId, groupedByInstance)}</td>
                      <td>{worker.runningCount}</td>
                      <td>{worker.queueLength}</td>
                      <td>{formatOldestTask(worker.oldestTask)}</td>
                      <td>{moment(worker.updatedAt).format('M/D/YY h:mm:ss a')}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Paper>
      )}

      {flags.queued && queueStatus?.queuedTasks && (
        <ExpansionPanel className={classes.expansionPanel} defaultExpanded={true}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />} className={classes.expansionSummary}>
            <Typography>
              Queued Tasks
              <span className={classes.taskCount}>({queueStatus.queuedTasks.length})</span>
            </Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <TaskTable tasks={queueStatus.queuedTasks} type="queued" showWorkerId={showWorkerId} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}

      {flags.running && queueStatus?.runningTasks && (
        <ExpansionPanel className={classes.expansionPanel} defaultExpanded={true}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />} className={classes.expansionSummary}>
            <Typography>
              Running Tasks
              <span className={classes.taskCount}>({queueStatus.runningTasks.length})</span>
            </Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <TaskTable tasks={queueStatus.runningTasks} type="running" showWorkerId={showWorkerId} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}

      {flags.completed && queueStatus?.completedTasks && (
        <ExpansionPanel className={classes.expansionPanel} defaultExpanded={false}>
          <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />} className={classes.expansionSummary}>
            <Typography>
              Completed Tasks
              <span className={classes.taskCount}>
                ({queueStatus.completedTasks.length})
                {queueStatus.completedTaskRetention && ` - Retention: ${queueStatus.completedTaskRetention}`}
              </span>
            </Typography>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <TaskTable tasks={queueStatus.completedTasks} type="completed" showWorkerId={showWorkerId} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}

      <LoadSimulatorModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleSubmitLoadSimulator} />
    </div>
  )
}

export const EventQueueMonitoring = () => {
  return (
    <PageHeader
      title="EventHandler Queue Monitoring"
      subTitle="Aggregated in-process handler queue across all api-server workers"
    >
      <EventQueueMonitoringComponent />
    </PageHeader>
  )
}
